import React, { useState } from "react";
import grids from "../responsive-fixes/grids.module.css";

interface Integration {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  color: string;
  connected: boolean;
  docsUrl?: string;
  envKey?: string;
  badge?: string;
}

const INTEGRATIONS: Integration[] = [
  // E-commerce & Marketplace
  { id: "shopify",    name: "Shopify",        category: "E-Commerce",    icon: "🛍️", color: "#96bf48", description: "Sync orders, inventory, products & abandoned carts", connected: true,  envKey: "SHOPIFY_ACCESS_TOKEN" },
  { id: "noon",       name: "Noon",           category: "Marketplace",   icon: "🌙", color: "#ffd700", description: "UAE's largest marketplace — product sync & order management", connected: false, badge: "UAE" },
  { id: "amazon_ae",  name: "Amazon.ae",      category: "Marketplace",   icon: "📦", color: "#ff9900", description: "Amazon UAE seller integration — listings & fulfillment",       connected: false, badge: "UAE" },
  { id: "trendyol",   name: "Trendyol",       category: "Marketplace",   icon: "🛒", color: "#f27a1a", description: "Turkey/MENA marketplace — product & order sync",               connected: false },
  { id: "namshi",     name: "Namshi",         category: "Marketplace",   icon: "👗", color: "#e91e63", description: "Fashion marketplace in MENA region",                           connected: false, badge: "UAE" },
  { id: "mumzworld",  name: "Mumzworld",      category: "Marketplace",   icon: "👶", color: "#ff6f61", description: "Baby & kids marketplace in UAE",                               connected: false, badge: "UAE" },
  // Communication
  { id: "ultramsg",   name: "UltraMsg",       category: "Messaging",     icon: "💬", color: "#25d366", description: "WhatsApp Business API — messages, media & webhooks",           connected: true,  envKey: "ULTRAMSG_TOKEN" },
  { id: "twilio",     name: "Twilio",         category: "Messaging",     icon: "📱", color: "#f22f46", description: "SMS + voice calling for customer outreach",                    connected: false },
  { id: "sendgrid",   name: "SendGrid",       category: "Email",         icon: "✉️",  color: "#1a82e2", description: "Transactional email — invoices, notifications, campaigns",    connected: false },
  // AI & Intelligence
  { id: "openai",     name: "OpenAI",         category: "AI",            icon: "🤖", color: "#10a37f", description: "GPT-4o-mini — user-facing AI, notifications, discipline",     connected: false, envKey: "OPENAI_API_KEY", badge: "Pending" },
  { id: "deepseek",   name: "DeepSeek",       category: "AI",            icon: "🧬", color: "#4f6ef7", description: "DeepSeek chat — AI fallback provider",                         connected: true,  envKey: "DEEPSEEK_API_KEY" },
  { id: "elevenlabs", name: "ElevenLabs",     category: "AI",            icon: "🎙️", color: "#9d6fff", description: "Text-to-speech for voice sales & OmniVoice",                  connected: true,  envKey: "ELEVENLABS_API_KEY" },
  { id: "huggingface",name: "HuggingFace",    category: "AI",            icon: "🤗", color: "#ffb41d", description: "Veridian Ginie — Qwen2.5-72B independent AI",                 connected: false, envKey: "HUGGINGFACE_TOKEN" },
  // Payments & Finance
  { id: "stripe",     name: "Stripe",         category: "Payments",      icon: "💳", color: "#6772e5", description: "Card payments & subscription billing",                         connected: false },
  { id: "network_me", name: "Network Int'l",  category: "Payments",      icon: "🏦", color: "#003f7f", description: "UAE payment gateway — Visa/MC/AMEX acceptance",               connected: false, badge: "UAE" },
  // Logistics
  { id: "aramex",     name: "Aramex",         category: "Logistics",     icon: "📮", color: "#e8212c", description: "GCC & MENA shipping — tracking & fulfillment",                connected: false, badge: "UAE" },
  { id: "fetchr",     name: "Fetchr",         category: "Logistics",     icon: "🚚", color: "#00b0ff", description: "UAE last-mile delivery & returns management",                  connected: false, badge: "UAE" },
  // Analytics
  { id: "google_analytics", name: "Google Analytics", category: "Analytics", icon: "📊", color: "#e37400", description: "Website traffic, conversion & SEO performance",          connected: false },
  { id: "meta_ads",   name: "Meta Ads",       category: "Marketing",     icon: "📣", color: "#1877f2", description: "Facebook & Instagram ad campaigns & ROAS tracking",           connected: false },
  { id: "google_ads", name: "Google Ads",     category: "Marketing",     icon: "🔎", color: "#4285f4", description: "Search & Shopping ads — performance & bidding",               connected: false },
];

const CATEGORIES = INTEGRATIONS.map(i => i.category).filter((v, i, a) => a.indexOf(v) === i);

const s: Record<string, React.CSSProperties> = {
  page: { padding: "24px", fontFamily: "'Space Grotesk', sans-serif", color: "#dae2fd" },
  card: {
    background: "rgba(19,27,46,0.7)", backdropFilter: "blur(14px)",
    border: "1px solid rgba(78,69,59,0.2)", borderRadius: 14, padding: "20px",
  },
};

const IntegrationsHub: React.FC = () => {
  const [filter,    setFilter]    = useState<string>("All");
  const [search,    setSearch]    = useState("");
  const [apiKeyMap, setApiKeyMap] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connected, setConnected]   = useState<Record<string, boolean>>(
    Object.fromEntries(INTEGRATIONS.filter(i => i.connected).map(i => [i.id, true]))
  );

  const filtered = INTEGRATIONS.filter(i => {
    const matchCat = filter === "All" || i.category === filter;
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const connectedCount = Object.values(connected).filter(Boolean).length;

  const handleConnect = async (integration: Integration) => {
    setConnecting(integration.id);
    await new Promise(r => setTimeout(r, 900));
    setConnected(prev => ({ ...prev, [integration.id]: true }));
    setConnecting(null);
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{
          width: 52, height: 52,
          background: "linear-gradient(135deg, rgba(200,191,255,0.2), rgba(184,213,141,0.1))",
          border: "1px solid rgba(200,191,255,0.2)", borderRadius: 14,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
        }}>🔌</div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, fontFamily: "'Manrope', sans-serif", color: "#ffd6a5" }}>
            Integrations Hub
          </h1>
          <p style={{ margin: 0, fontSize: 12, color: "#4a6a7a" }}>
            {connectedCount}/{INTEGRATIONS.length} connected · One-click API connections
          </p>
        </div>
        {/* Connected indicator */}
        <div style={{
          marginLeft: "auto",
          background: "rgba(184,213,141,0.1)", border: "1px solid rgba(184,213,141,0.2)",
          borderRadius: 10, padding: "8px 16px", fontSize: 13, color: "#b8d58d", fontWeight: 700,
        }}>
          ✅ {connectedCount} Active
        </div>
      </div>

      {/* Search + Category filter */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search integrations…"
          style={{
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,191,255,0.15)",
            borderRadius: 9, padding: "8px 14px",
            color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12,
            width: 220,
          }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["All", ...CATEGORIES].map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: "6px 14px",
                background: filter === cat ? "rgba(200,191,255,0.15)" : "transparent",
                border: filter === cat ? "1px solid rgba(200,191,255,0.3)" : "1px solid rgba(78,69,59,0.15)",
                borderRadius: 7, cursor: "pointer",
                color: filter === cat ? "#c8bfff" : "#4a6070",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600,
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grid — M2 fix (audit): minmax(300px) overflows below 300px viewport. */}
      <div className={grids["responsive-grid"]} style={{ gap: 14 }}>
        {filtered.map(integration => {
          const isConnected = connected[integration.id];
          const isConnecting = connecting === integration.id;
          return (
            <div
              key={integration.id}
              style={{
                ...s.card,
                border: isConnected ? `1px solid ${integration.color}30` : "1px solid rgba(78,69,59,0.2)",
                transition: "border-color 0.2s",
              }}
            >
              {/* Card header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{
                  width: 40, height: 40,
                  background: `${integration.color}15`,
                  border: `1px solid ${integration.color}30`,
                  borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, flexShrink: 0,
                }}>
                  {integration.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#dae2fd" }}>{integration.name}</span>
                    {integration.badge && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: "0.05em",
                        background: "rgba(255,214,165,0.12)", border: "1px solid rgba(255,214,165,0.25)",
                        borderRadius: 4, padding: "1px 5px", color: "#ffd6a5",
                      }}>{integration.badge}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "#3a5060" }}>{integration.category}</div>
                </div>
                {/* Status dot */}
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: isConnected ? "#b8d58d" : "rgba(255,255,255,0.1)",
                  boxShadow: isConnected ? "0 0 8px rgba(184,213,141,0.6)" : "none",
                  flexShrink: 0,
                }} />
              </div>

              {/* Description */}
              <p style={{ margin: "0 0 12px", fontSize: 11.5, color: "#5a7090", lineHeight: 1.5 }}>
                {integration.description}
              </p>

              {/* API key input if needed */}
              {integration.envKey && !isConnected && (
                <div style={{ marginBottom: 10 }}>
                  <input
                    placeholder={`Enter ${integration.envKey}`}
                    value={apiKeyMap[integration.id] || ""}
                    onChange={e => setApiKeyMap(prev => ({ ...prev, [integration.id]: e.target.value }))}
                    type="password"
                    style={{
                      width: "100%", boxSizing: "border-box",
                      background: "rgba(255,255,255,0.03)", border: "1px solid rgba(200,191,255,0.1)",
                      borderRadius: 7, padding: "7px 10px",
                      color: "#9ab0c9", fontFamily: "monospace", fontSize: 11,
                    }}
                  />
                </div>
              )}

              {/* Action button */}
              <button
                onClick={() => !isConnected && handleConnect(integration)}
                disabled={isConnecting}
                style={{
                  width: "100%", padding: "8px 0",
                  background: isConnected ? "rgba(184,213,141,0.08)" : `${integration.color}15`,
                  border: isConnected ? "1px solid rgba(184,213,141,0.2)" : `1px solid ${integration.color}30`,
                  borderRadius: 8, cursor: isConnected ? "default" : "pointer",
                  color: isConnected ? "#b8d58d" : integration.color,
                  fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 700,
                  transition: "all 0.18s",
                }}
              >
                {isConnecting ? "Connecting…" : isConnected ? "✅ Connected" : "Connect"}
              </button>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", color: "#2a3a4a", padding: "60px 0", fontSize: 13 }}>
          No integrations match your search.
        </div>
      )}

      {/* Footer note */}
      <div style={{
        ...s.card, marginTop: 20,
        fontSize: 11, color: "#2a3a4a",
      }}>
        💡 API keys are stored in <code style={{ color: "#c8bfff" }}>.env</code> on the server and never exposed client-side.
        For production, use Docker secrets or a vault service.
      </div>
    </div>
  );
};

export default IntegrationsHub;
